import { LoadingOverlay, useMantineTheme } from "@mantine/core";
import { modals } from "@mantine/modals";
import { createRouter, RegisteredRouter, RouterProvider, ValidateLinkOptions } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo } from "react";
import ReactDOM from "react-dom/client";
import toast from "react-hot-toast";
import { Provider } from "react-redux";

import { LinkTo } from "./@types/routing";
import { NotFound } from "./components/_NotFound";
import { Providers } from "./components/_Providers";
import { RequestMonitorPanel } from "./components/RequestMonitorPanel";
import { ToasterWithMax } from "./components/ToasterWithMax";
import { TOASTER_ID_KEYS } from "./constants";
import { useSession, useWindowLocationHost } from "./hooks";
import { MODAL_HANDLER } from "./lib/Mantine";
import { routeMasks } from "./pages/-maskedRoutes";
import { routeTree } from "./routeTree.gen";
import { store } from "./store";

import "@/lib/zod/zod.config";

dayjs.extend(relativeTime);
dayjs.locale("pt-br");

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  //TODO - Rever implementação de tipos
  interface HistoryState<TRouter extends RegisteredRouter = RegisteredRouter, TOptions = unknown> {
    redirectTo?: LinkTo;
    redirect?: ValidateLinkOptions<TRouter, TOptions>;
    driverId?: number;
    companyId?: number;
    shippingCompanyId?: number;
    shippingCompany?: {
      id?: number;
      name?: string;
    };
    company?: {
      id?: number;
      name?: string;
    };
  }
}

export interface RouterContext {
  theme: ReturnType<typeof useMantineTheme>;
  store: typeof store;
  modal: {
    handler: typeof MODAL_HANDLER;
    new: typeof modals;
  };
  libs: {
    toast: typeof toast;
  };
  state: {
    windowLocationHost: string | null;
  };
}

// Create a new router instance
export const router = createRouter({
  routeTree,
  routeMasks,
  unmaskOnReload: true,
  // defaultPreload: "intent",
  // defaultStaleTime: Infinity,
  // defaultPreloadStaleTime: 0,
  // gcTime: 0,
  //@ts-expect-error Se trata apenas de um erro de tipagem, os tipos faltantes são definidos em contextData
  context: {
    store,
    modal: {
      handler: MODAL_HANDLER,
      new: modals
    },
    state: {
      windowLocationHost: null
    }
  }
});

function InnerApp() {
  const session = useSession();
  const theme = useMantineTheme();
  const { windowLocationHost } = useWindowLocationHost();

  const contextData = useMemo(
    () => ({
      theme,
      libs: {
        toast
      },
      state: {
        windowLocationHost
      },
      session
    }),
    [windowLocationHost, theme, session]
  );

  return (
    <>
      <RouterProvider
        router={router}
        context={contextData}
        defaultNotFoundComponent={() => <NotFound />}
        defaultPendingComponent={() => <LoadingOverlay visible loaderProps={{ color: theme.primaryColor, size: "xl", type: "bars" }} overlayProps={{ backgroundOpacity: 1 }} />}
      />
      <TanStackRouterDevtools router={router} position="bottom-right" />
      <RequestMonitorPanel />
    </>
  );
}

// Componente que conecta os contextos
function App() {
  return (
    <Provider store={store}>
      <Providers.Mantine>
        <InnerApp />

        <ToasterWithMax position="bottom-right" toasterId={TOASTER_ID_KEYS.DEFAULT} />
      </Providers.Mantine>
    </Provider>
  );
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
